import { Injectable } from '@angular/core';
import {FormControl, ValidatorFn} from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class FormUtilityService {

  constructor() { }

  /**
   * Clear form controls of validators and errors.
   * @param formControls Can be a single form control or an array of form controls.
   */
  public resetFormControls(formControls: FormControl[] | FormControl): void;
  public resetFormControls(formControls: FormControl): void {
    if (Array.isArray(formControls)) {
      formControls.forEach(control => control.reset());
    } else {
      formControls.reset();
    }
  }

  /**
   * Add validators to form control(s), and update value and validity.
   * @param target Can be a single form control with validators or an array of form controls with validators.
   */
  public addValidators(target: {control: FormControl, validators: ValidatorFn | ValidatorFn[] }[] | {control: FormControl, validators: ValidatorFn | ValidatorFn[] }): void;
  public addValidators(target: {control: FormControl, validators: ValidatorFn | ValidatorFn[] }): void {
    if (Array.isArray(target)) {
      target.forEach(({control, validators}) => {
        control.addValidators(validators);
        control.updateValueAndValidity({onlySelf: true});
      });
    } else {
      target.control.addValidators(target.validators);
      target.control.updateValueAndValidity({onlySelf: true});
    }
  }

  /**
   * Remove validators from form control(s), and update value and validity.
   * @param target Can be a single form control with validators or an array of form controls with validators.
   */
  public removeValidators(target: {control: FormControl, validators: ValidatorFn | ValidatorFn[] }[] | {control: FormControl, validators: ValidatorFn | ValidatorFn[] }): void;
  public removeValidators(target: {control: FormControl, validators: ValidatorFn | ValidatorFn[] }): void {
    if (Array.isArray(target)) {
      target.forEach(({control, validators}) => {
        control.removeValidators(validators);
        control.updateValueAndValidity({onlySelf: true});
      });
    } else {
      target.control.removeValidators(target.validators);
      target.control.updateValueAndValidity({onlySelf: true});
    }
  }

  /**
   * Clear validators from form control(s), and optionally nullify errors.
   * @param formControls Can be a single form control or an array of form controls.
   * @param nullifyErrors If true, errors will be set to null.
   */
  public clearValidators(formControls: FormControl[] | FormControl, nullifyErrors?: boolean): void;
  public clearValidators(formControl: FormControl, nullifyErrors?: boolean): void {
    if (Array.isArray(formControl)) {
      formControl.forEach(control => {
        control.clearValidators();
        if (nullifyErrors) {
          control.setErrors(null);
          control.markAsPristine();
        }
        control.updateValueAndValidity();
      });
    } else {
      formControl.clearValidators();
      if (nullifyErrors) {
        formControl.setErrors(null);
        formControl.markAsPristine();
      }
      formControl.updateValueAndValidity();
    }
  }

  /**
   * Set value for form control(s) and update their validity. Mark as pristine if specified.
   * @param value
   * @param formControls
   * @param markAsPrestine
   */
  public setValueAndUpdate<T>(value: T, formControls: FormControl[] | FormControl, markAsPrestine = false): void {
    if (Array.isArray(formControls)) {
      formControls.forEach(control => {
        control.setValue(value);
        control.updateValueAndValidity();
        if (markAsPrestine) {
          control.markAsPristine();
        }
      });
    } else {
      formControls.setValue(value);
      formControls.updateValueAndValidity();
      if (markAsPrestine) {
        formControls.markAsPristine();
      }
    }
  }
}

import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

export function logInvalidControls(control: AbstractControl, name: string = 'root', depth: number = 0): void {
  const prefix = '--'.repeat(depth);

  if (control.invalid) {
    console.log(`${prefix}${name} (invalid)`, control.errors ?? '');
  }

  if (control instanceof FormGroup) {
    Object.entries(control.controls).forEach(([key, child]) => {
      logInvalidControls(child, key, depth + 1);
    });
  } else if (control instanceof FormArray) {
    control.controls.forEach((child, index) => {
      logInvalidControls(child, `[${index}]`, depth + 1);
    });
  }
}
